import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Star, CheckCircle } from "lucide-react";
import axios from "axios";
import { API } from "../config/constants";

export default function ReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [formData, setFormData] = useState({ name: "", organization: "", rating: 5, text: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    axios.get(`${API}/reviews`).then((res) => setReviews(res.data)).catch(console.error);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, formData);
      setSubmitted(true);
      setFormData({ name: "", organization: "", rating: 5, text: "" });
      const res = await axios.get(`${API}/reviews`);
      setReviews(res.data);
    } catch (error) {
      console.error("Error submitting review:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="reviews-page" className="pt-20">
      <section className="py-16 bg-[#0F2137]">
        <div className="container-custom">
          <span className="section-kicker">Testimonials</span>
          <h1 className="text-5xl font-bold text-white">What Our Clients Say</h1>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="container-custom">
          {reviews.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.map((review, index) => (
                <motion.div key={review.id || `review-${index}`} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} viewport={{ once: true }} className="bg-[#F8F9FA] p-6 rounded-xl" data-testid={`review-${index}`}>
                  <div className="flex gap-1 mb-4">{[...Array(review.rating)].map((_, i) => (<Star key={`star-${review.id}-${i}`} className="text-amber-400 fill-amber-400" size={20} />))}</div>
                  <p className="text-[#495057] mb-4 leading-relaxed">"{review.text}"</p>
                  <div className="border-t border-gray-200 pt-4">
                    <p className="font-semibold text-[#0F2137]">{review.name}</p>
                    {review.organization && <p className="text-[#495057] text-sm">{review.organization}</p>}
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <p className="text-center text-[#495057]">No reviews yet. Be the first!</p>
          )}
        </div>
      </section>

      <section className="py-24 bg-[#F8F9FA]">
        <div className="container-custom max-w-2xl">
          <div className="text-center mb-12">
            <span className="section-kicker">Share Your Experience</span>
            <h2 className="text-4xl font-bold text-[#0F2137]">Leave a Review</h2>
          </div>
          {submitted ? (
            <div className="bg-white p-8 rounded-xl text-center">
              <CheckCircle className="mx-auto text-green-500 mb-4" size={64} />
              <h3 className="text-2xl font-bold text-[#0F2137] mb-2">Thank You!</h3>
              <p className="text-[#495057]">Your review has been submitted.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white p-8 rounded-xl shadow-lg" data-testid="review-form">
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#0F2137] mb-2">Your Name *</label>
                <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-4 py-3 bg-[#F8F9FA] border border-[#E9ECEF] rounded-lg" data-testid="review-name-input" />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#0F2137] mb-2">Organization (Optional)</label>
                <input type="text" value={formData.organization} onChange={(e) => setFormData({ ...formData, organization: e.target.value })} className="w-full px-4 py-3 bg-[#F8F9FA] border border-[#E9ECEF] rounded-lg" data-testid="review-org-input" />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#0F2137] mb-2">Rating *</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button key={star} type="button" onClick={() => setFormData({ ...formData, rating: star })} data-testid={`rating-star-${star}`} className="p-1">
                      <Star size={32} className={star <= formData.rating ? "text-amber-400 fill-amber-400" : "text-gray-300"} />
                    </button>
                  ))}
                </div>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#0F2137] mb-2">Your Review *</label>
                <textarea required rows={4} value={formData.text} onChange={(e) => setFormData({ ...formData, text: e.target.value })} className="w-full px-4 py-3 bg-[#F8F9FA] border border-[#E9ECEF] rounded-lg resize-none" data-testid="review-text-input" />
              </div>
              <button type="submit" disabled={isSubmitting} data-testid="submit-review-btn" className="w-full btn-primary disabled:opacity-50">{isSubmitting ? "Submitting..." : "Submit Review"}</button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
